import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import _ from 'lodash';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { ROLE_NAMES } from '../users/UserConstants';
import { LDAP_STATUS_FAIL, LDAP_STATUS_SUCCESS, LDAP_STATUS_UNKNOWN, LDAP_TEST_STATUSES } from './SsoAdminConstants';
import { NgpLabelValue } from '../../form/NgpLabelValue';

const usingAnonymousBindingText = i18n._(t`Using anonymous binding`);
const noUsernameSpecifiedText = i18n._(t`No username specified`);
const noUsernameOrPasswordSpecifiedText = i18n._(t`Authentication requires a username and password`);

export function processVerifyResults(results, verifyConfig) {
    function setTestStatus(name, status, statusText) {
        if (name) {
            const test = _.find(ldapTestStatuses, { name });
            test.status = status;
            test.statusText = statusText;
        } else {
            _.each(ldapTestStatuses, (test) => {
                test.status = status;
                test.statusText = statusText;
            });
        }
    }

    const ldapTestStatuses = _.cloneDeep(LDAP_TEST_STATUSES);
    const error = _.get(results, 'error');
    const testUserUsername = _.get(verifyConfig, 'testUser.username');
    const testUserPassword = _.get(verifyConfig, 'testUser.password');
    const ldapUser = _.get(results, 'ldapUser');
    const ldapUserRole = _.get(results, 'ldapUserRole');
    const ldapUserGroups = _.get(results, 'ldapUserGroups');
    const ldapUserAuthenticated = _.get(results, 'ldapUserAuthenticated');
    const userResult = {};

    userResult.ldapUser = ldapUser;
    userResult.ldapUserRole = ROLE_NAMES[ldapUserRole];
    userResult.ldapUserGroupsText = _.map(ldapUserGroups, 'name').join(', ');

    if (!error) {
        setTestStatus('connect', LDAP_STATUS_SUCCESS);
        setTestStatus('bind', LDAP_STATUS_SUCCESS);

        if (_.get(verifyConfig, 'anonymous', false) === true) {
            setTestStatus('bind', LDAP_STATUS_UNKNOWN, usingAnonymousBindingText);
        }

        if (ldapUser) {
            setTestStatus('findUser', LDAP_STATUS_SUCCESS);
        } else if (!testUserUsername) {
            setTestStatus('findUser', LDAP_STATUS_UNKNOWN, noUsernameSpecifiedText);
        }

        if (ldapUserAuthenticated) {
            setTestStatus('authUser', LDAP_STATUS_SUCCESS);
        } else if (!testUserPassword) {
            setTestStatus('authUser', LDAP_STATUS_UNKNOWN, noUsernameOrPasswordSpecifiedText);
        }

        return { userResult, testStatuses: ldapTestStatuses };
    }

    const { type, message } = error;
    _.each(ldapTestStatuses, (test) => {
        if (_.includes(test.assocErrors, type)) {
            setTestStatus(test.name, LDAP_STATUS_FAIL, message);

            // stop going forward...
            return false;
        }

        setTestStatus(test.name, LDAP_STATUS_SUCCESS);
    });

    return { userResult, testStatuses: ldapTestStatuses };
}

export function buildVerifyResult(result, classes) {
    const { userResult, testStatuses } = result;
    const statusConfig = {
        unknown: {
            className: classes.resultUnknown,
            icon: <HelpOutlineIcon />,
        },
        fail: {
            className: classes.resultFail,
            icon: <ErrorOutlineIcon />,
        },
        success: {
            className: classes.resultSuccess,
            icon: <CheckCircleOutlineIcon />,
        },
    };

    if (!testStatuses) {
        return null;
    }

    const statusHtml = _.map(testStatuses, (testStatus) => {
        return (
            <div
                key={_.uniqueId()}
                className={`${classes.verifyTestResult} ${statusConfig[testStatus.status].className}`}
            >
                <div className={classes.verifyTestResultText}>{statusConfig[testStatus.status].icon}</div>
                <div className={classes.verifyTestResultText}>
                    <div className={classes.verifyTestResultTitle}>{testStatus.title}</div>
                    <div className={classes.verifyTestResultStatus}>{testStatus.statusText}</div>
                </div>
            </div>
        );
    });

    return (
        <>
            <div className={classes.verifyTestResults}>{statusHtml}</div>
            {userResult?.ldapUser?.userName && (
                <div className={classes.userInfoContainer}>
                    <NgpLabelValue dataLabel={<Trans>Username</Trans>} dataValue={userResult?.ldapUser?.userName} />
                    <NgpLabelValue dataLabel={<Trans>Email</Trans>} dataValue={userResult?.ldapUser?.email} />
                    <NgpLabelValue dataLabel={<Trans>First Name</Trans>} dataValue={userResult?.ldapUser?.firstName} />
                    <NgpLabelValue dataLabel={<Trans>Last Name</Trans>} dataValue={userResult?.ldapUser?.lastName} />
                    <NgpLabelValue
                        dataLabel={<Trans>Group Memberships</Trans>}
                        dataValue={userResult?.ldapUserGroupsText}
                    />
                    <NgpLabelValue
                        dataLabel={<Trans>Role Assignment</Trans>}
                        dataValue={_.get(
                            userResult,
                            'ldapUserRole',
                            <Trans>
                                This user would not be allowed to log into nGeniusPULSE as the account did not match any
                                group roles, and the default role is Unauthorized
                            </Trans>
                        )}
                    />
                </div>
            )}
        </>
    );
}
